// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/menage.js — LES LEVIERS DE LA FAMILLE MÉNAGE.
//
// Périmètre vérifié dans `api/beds24-mapping.js`. 23 leviers déclarés ici —
// dont les 8 « portes de terrain » du 2026-09-16 (vague 5), montées parce que
// les écrans Ménages / Incidents / Stock n'avaient AUCUNE action à composer —
// et 7 actions ÉCARTÉES : le détail de chaque écart (raison + ligne de
// raisonnement) voyage avec l'entrée absente, juste avant le bloc suivant,
// pour que personne ne le redécouvre en relisant le handler.
//
// ⚠️ Comme `operateur.js` : ce module ne fait rien. Il décrit. `endpoint` est
// l'URL que le NAVIGATEUR de Dimitri appelle, avec SON jeton.

const LEVIERS_MENAGE = Object.freeze({
  // ── CONSIGNES INTERNES (menage_instructions, ADR-302) ────────────────────
  //
  // ⚠️ `menage_instruction_delete` (SUPPRESSION DÉFINITIVE) N'EST PAS DÉCLARÉE
  // ICI, et ne doit jamais l'être : c'est un DELETE dur (E1), sans tombstone
  // ni deleted_at — `lib/menage-instructions-handlers.js` l'écrit noir sur
  // blanc (« la ligne disparaît, elle n'est pas archivée »). Le motif RGPD qui
  // justifie l'action (une donnée personnelle de voyageur écrite par erreur
  // dans le texte libre) est justement celui qui interdit qu'une intention
  // composée par l'opérateur en tienne lieu : Dimitri doit CONSTATER le
  // contenu problématique lui-même avant d'effacer, pas cliquer sur une carte
  // qui ne le lui montre pas. `menage_instruction_archive`, réversible, couvre
  // déjà le rangement courant.
  menage_instruction_save: {
    ecran: "menages",
    endpoint: "/api/beds24-mapping",
    action: "menage_instruction_save",
    args_autorises: [
      "id",
      "logement_id",
      "target_agent_email",
      "title",
      "body",
      "priority",
    ],
    // ⚠️ DEUX REPÈRES FACULTATIFS, ET C'EST CE QUI GARDE LA CRÉATION VIVANTE.
    // Ce levier écrit une consigne NEUVE quand `id` manque : exiger le repère
    // refuserait le geste le plus courant. Le NOM déclenche la recherche (« la
    // consigne technique »), le LOGEMENT la cloisonne — et sans logement dit,
    // le titre est cherché dans tout le parc (D7, prod du 22/09) : unique ⇒
    // la consigne, et son logement avec elle ; sur plusieurs logements ⇒ « de
    // quel logement ? » en les nommant ; absent ⇒ la question D2, comme avant.
    args_resolus: {
      logement_id: { objet: "logement", par: ["logement"], requis: [] },
      id: { objet: "consigne_menage", par: ["nom", "logement"], requis: [] },
    },
    // ⚠️ Liste fermée trouvée par le balayage qui SUIT LES HELPERS APPELÉS —
    // la validation ne vit pas dans le handler mais dans une fonction qu'il
    // appelle. Un balayage limité au corps du handler l'aurait manquée.
    args_valeurs: {
      priority: ["normale", "importante"],
    },
    titre: "Écrire une consigne interne ménage",
    constatable:
      "la consigne apparaît (ou change) sur la fiche du logement ou de l'agent, dans l'onglet Ménage",
    // Vague 9 : `body`/`title` sont le TEXTE de la consigne elle-même — la
    // même exception que Dimitri a posée pour les brouillons (« le jour où il
    // saura proposer un brouillon que je réécris, ça servira »). Ni argent ni
    // tiers : la consigne s'adresse à un agent DEDANS l'entreprise.
    sujet: "terrain",
    cout: ["rien"],
    // L'agent la lit dans SON application : rien ne quitte FBZ.
    ou_ca_part: ["agent"],
    sans_retour:
      "le texte remplacé n'est gardé nulle part : une consigne modifiée écrase l'ancienne, il n'y a pas d'historique de version",
    ne_fait_pas:
      "aucun message ne part chez l'agent : il découvre la consigne en ouvrant son écran, pas par une notification",
    apercu: {
      lecture: "menage_instruction_lire",
      depend_de: ["id", "logement_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Écris une consigne pour l'équipe sur ce logement",
      "Note qu'il faut penser aux poubelles au Cocon",
      "Change la consigne de ménage de ce logement",
    ],
    vague: 9,
    // ⚠️ `false` DEPUIS LE 2026-09-09, et c'est une correction. Ce levier
    // exigeait un motif que le handler NE LIT PAS : la carte l'imposait à
    // Dimitri, et son texte partait dans le vide. Une friction sans effet use
    // la confiance dans celles qui en ont un — les motifs qui comptent
    // (taire une alerte, lever une reprise, débloquer un livret) sont
    // enregistrés, eux.
    motif_requis: false,
    autonome: false,
  },

  menage_instruction_archive: {
    ecran: "menages",
    endpoint: "/api/beds24-mapping",
    action: "menage_instruction_archive",
    args_autorises: ["id", "archived"],
    // ⚠️ L'UNIQUE CONSIGNE DU PARC EST DÉJÀ ARCHIVÉE (mesuré le 2026-09-22).
    // Le résolveur la fait remonter EN LE DISANT, et la garde prévient que le
    // geste utile est l'inverse — un candidat archivé qui disparaîtrait en
    // silence ferait ré-archiver ce qui l'est déjà.
    args_resolus: {
      id: { objet: "consigne_menage", par: ["nom", "logement"] },
    },
    titre: "Archiver ou réactiver une consigne",
    constatable:
      "la consigne disparaît (ou revient) dans la vue de l'agent ; elle reste visible côté admin/orchestrateur avec sa date d'archivage",
    // 1 ✔ (le booléen `archived` défait le geste) · 2 ✔ (rien ne sort, la
    // ligne reste en base) · 3 ✔ (état visible immédiatement) · 4 ✔ (aucun
    // champ numérique). Rangement/clôture pur.
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour: null, // le booléen `archived` défait le geste, dans les deux sens
    ne_fait_pas:
      "il ne supprime rien : la consigne reste en base et reste visible côté admin, avec sa date d'archivage — seule la vue de l'agent la perd",
    apercu: {
      lecture: "menage_instruction_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Range cette consigne, elle ne sert plus",
      "Remets cette consigne en service",
      "Enlève cette note de l'écran des agents",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
  },

  // ── ASSIGNER UN MÉNAGE À UN AGENT (2026-09-13, réclamé par Dimitri) ──────
  //
  // Jusqu'ici l'assignation n'existait qu'à l'écran (UPDATE client sous RLS,
  // `MenageTodayView.handleReassign`) : aucune action à composer. Celle-ci vit
  // dans `lib/menage-assign-handlers.js`.
  //
  // ⚠️ L'AGENT N'EST PAS UN ARGUMENT COMPOSÉ. C'est un compte, donc un
  // e-mail — que le moteur d'événements n'a pas le droit de lire et que le
  // modèle ne connaît pas. Il est CHOISI par Dimitri sur la carte, dans la
  // liste que `menage_agents_assignables` rend (`args_choisis`) ; le geste
  // voyage avec l'identifiant du compte, jamais l'adresse.
  //
  // Les trois décisions de Dimitri (2026-09-13) : ÉCRASE l'assignation en
  // place (dit avant le clic : `sans_retour` + `actuel` dans la liste) · un
  // ménage terminé ne se réassigne PAS (409 `run_termine`, jamais un UPDATE
  // à vide) · assignable = accès au logement (`menage_has_log_access`, le
  // même prédicat pour la liste et pour l'écriture).
  menage_run_assign: {
    ecran: "menages",
    endpoint: "/api/beds24-mapping",
    action: "menage_run_assign",
    args_autorises: ["run_id"],
    // ⚠️ LE `run_id` NE SE PRONONCE PAS — ET LE MODÈLE NE L'A JAMAIS VU. Ni le
    // relevé (`lib/operateur-jour.js` lit `menage_runs` SANS `id`) ni la
    // lecture d'outil (`lib/lectures-chargeurs.js` le SELECT-e et ne le rend
    // pas) ne le font sortir : un `run_id` composé est forcément inventé. Le
    // modèle pose donc « le ménage du Cocon Naturel samedi » dans `vise`, et
    // `lib/menage-repere.js` va chercher la ligne, avec le jeton de Dimitri.
    // L'argument RESTE dans `args_autorises` (sinon `filtrerArgs` refuse la
    // carte au clic, et l'aperçu ne pourrait plus le citer).
    args_resolus: {
      run_id: { objet: "menage_run", par: ["logement", "quand"] },
    },
    args_choisis: {
      agent_id: {
        lecture: "menage_agents_assignables",
        depend_de: ["run_id"],
        etiquette: "Agent",
      },
    },
    titre: "Assigner un ménage à un agent",
    constatable:
      "le ménage porte le nouvel agent dans l'écran Ménages ; s'il en avait déjà un, celui-ci ne le voit plus et son bandeau lui annonce le retrait",
    // Réglage interne (un identifiant, un compte de l'équipe) : ni argent, ni
    // voyageur, ni sortie de chez FBZ. Refaisable — mais l'assignation d'avant
    // n'est mémorisée nulle part, d'où la phrase ci-dessous.
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    // ⚠️ LA LIGNE LA PLUS IMPORTANTE DE CETTE CARTE : Dimitri peut croire
    // qu'assigner prévient. Ça ne prévient pas.
    ne_fait_pas:
      "aucun message ne part : ni SMS, ni e-mail, ni notification. Le nouvel agent découvre le ménage en ouvrant son écran ; le précédent, lui, y voit un bandeau de retrait.",
    // L'agent ACTUEL est montré par la liste `menage_agents_assignables`
    // (champ `actuel`) ; le reste du ménage — logement, date, heure fixée,
    // état — vient de `menage_run_lire` (2026-09-16, portes de terrain).
    apercu: {
      lecture: "menage_run_lire",
      depend_de: ["run_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "Donne ce ménage à Karim",
      "Change l'agent sur le ménage de demain",
      "C'est Sofia qui fait le Cocon jeudi",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
    sans_retour:
      "si le ménage était déjà assigné, cet agent en est retiré ; l'assignation d'avant n'est pas mémorisée — pour la rendre, il faut la refaire",
    // E1 ✔ (une colonne, refaisable) · E2 ✔ · E3 ✔ (même colonne que l'écran,
    // le trigger d'événements agent écrit le retrait/ajout tout seul) ·
    // E4 ✔ (0 montant) · E5 ✔ (rowcount-check + compare-and-set).
  },

  // ── CLÔTURE D'UN MÉNAGE PAR L'AGENT ──────────────────────────────────────
  //
  // ⚠️ `menage_run_complete` ÉCARTÉ (E4), vérifié dans `lib/menage-run-complete.js`.
  // Passer un run en `completed`, ce n'est pas ranger une ligne : l'en-tête du
  // handler le dit dans ses propres mots — « ménage fait = dû au prestataire »,
  // et il cite l'article 7.2 du contrat de location (l'état du logement est
  // constaté par des photos horodatées qui « font foi »). Le geste ATTESTE donc
  // deux faits qu'on n'a pas observés : que le ménage a eu lieu, et qu'il est
  // dû. E4 est né exactement de ça.
  //
  // ⚠️ ET LA GARDE QUI PROTÈGE CET ATTESTAT NE S'APPLIQUE PAS À DIMITRI. Le
  // handler exempte l'ADMIN de la garde photo (≥ 1 photo de pièce, 422 sinon) :
  // cliquée avec son jeton, une carte composée par l'opérateur clôturerait un
  // run SANS la preuve que l'action existe pour exiger. La seule protection
  // réelle sauterait précisément sur le chemin qu'on ouvrirait.
  //
  // Le chemin de correction de Dimitri existe déjà et est meilleur : le bouton
  // « ✏️ Remplir / Corriger » de ResaV2Modal (AdminChecklistEditor, ADR-207),
  // où il voit la checklist et les photos avant de trancher.

  // ── CONTRÔLE DES MÉNAGES (P1/P2, GO Dimitri 2026-08-17) ──────────────────
  //
  // ⚠️ ÉCARTÉS : `controle_create` (le verdict + `points[].texte` sont une
  // OBSERVATION DE TERRAIN — ce que le contrôleur a vu en se déplaçant dans le
  // logement ; l'opérateur n'a jamais mis les pieds sur place, composer ce
  // texte à sa place serait fabriquer un constat), `controle_photo_upload` et
  // `controle_agent_photo_reprise` (la preuve est une PHOTO — un binaire que
  // l'opérateur ne peut pas produire, et qui n'est de toute façon qu'une pièce
  // jointe au même constat de terrain), `controle_agent_point_fait` (l'action
  // ne s'exécute QUE si `run.assigned_provider_email === user.email` : cliquée
  // avec le jeton de Dimitri, elle échoue systématiquement en 404 — elle ne
  // peut être exécutée qu'AVEC L'IDENTITÉ de l'agent assigné, jamais celle de
  // Dimitri, ce qui contredit l'invariant même du bras droit).
  controle_designer: {
    ecran: "menage-controle",
    endpoint: "/api/beds24-mapping",
    action: "controle_designer",
    args_autorises: ["run_id", "logement_id", "controleur_email"],
    titre: "Désigner le contrôleur d'un ménage",
    constatable:
      "le contrôleur désigné apparaît sur la carte du ménage (ou par défaut sur la fiche du logement)",
    // Réglage borné : un email ∈ pool (pour l'orchestrateur) ou libre pour
    // l'admin, une clé technique (run_id/logement_id) — pas de texte ni de
    // montant. Réversible (upsert / update, redésignable à volonté).
    // ⚠️ VAGUE 2 DEPUIS LE 2026-09-09, sur décision de Dimitri. Ce levier était
    // en vague 4 uniquement parce qu'elle avait été définie comme « réglages
    // bornés » — pas parce qu'il touche à l'argent ou aux voyageurs. Il ne fait
    // ni l'un ni l'autre : il range une décision interne, et se défait.
    // Ses mots : « pour les trois qui ne touchent ni l'argent ni mes voyageurs :
    // ouvre les deux premiers ».
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour: null, // redésignable à volonté (upsert)
    ne_fait_pas:
      "il ne crée aucun contrôle et ne prévient personne : il dit QUI contrôlera, le contrôle lui-même reste à faire sur place",
    apercu_absent:
      "aucune lecture ne rend le contrôleur en place (celui du run, ou celui par défaut du logement) : il faudrait `controle_designation_lire`. Désigner sans voir qui était désigné, c'est écraser à l'aveugle.",
    dit_comme: [
      "C'est Karim qui contrôlera ce ménage",
      "Mets quelqu'un sur le contrôle de ce logement",
      "Change le contrôleur du Nid",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
  },

  controle_reprise_decider: {
    ecran: "menage-controle",
    endpoint: "/api/beds24-mapping",
    action: "controle_reprise_decider",
    args_autorises: ["controle_id", "choix", "raison", "agent_email"],
    titre: "Décider d'une reprise après contrôle",
    constatable:
      "le statut de la reprise change à l'écran (repris par moi / renvoyé à un agent / levé) — une tâche ou une réassignation apparaît selon le choix",
    // ADMIN SEUL côté handler. `choix` est une liste FERMÉE ('moi'|'agent'|
    // 'bon') ; `raison` est le motif tapé par Dimitri quand il lève la reprise
    // (le serveur l'exige, ≥ 3 caractères) — même famille que `motif` sur
    // `veille_silence`, jamais un texte que l'opérateur invente.
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour:
      "la décision ne se rejoue pas : une reprise levée, reprise par soi ou renvoyée à un agent a changé d'état, et le motif écrit reste au journal",
    ne_fait_pas:
      "il ne refait pas le ménage et n'envoie aucun message : selon le choix, il crée une tâche ou une réassignation que l'agent verra dans son écran",
    apercu_absent:
      "il faudrait `controle_reprise_lire` pour montrer ce que le contrôleur a relevé (les points, les photos) avant de trancher. Décider d'une reprise sans voir son constat est exactement ce que l'aperçu doit empêcher.",
    dit_comme: [
      "La reprise, je la fais moi-même",
      "Renvoie l'agent refaire ce ménage",
      "C'est bon, lève la reprise sur ce contrôle",
    ],
    vague: 2,
    motif_requis: true,
    // Le handler lit `raison` (et l'exige quand on lève une reprise).
    motif_champ: "raison",
    autonome: false,
  },

  // ── DÉPÔT CONSOMMABLES / SIGNALEMENT DE MANQUE (2026-08-21) ──────────────
  stock_signaler: {
    ecran: "stock",
    endpoint: "/api/beds24-mapping",
    action: "stock_signaler",
    args_autorises: ["logement_id", "echeance", "cs_id", "texte"],
    // ⚠️ L'ARTICLE EST FACULTATIF ICI (`requis: []`) : un manque peut se
    // signaler en texte seul (« il manque du produit ménager »). S'il est
    // NOMMÉ, il se résout — et un nom inconnu du catalogue REFUSE (aucun
    // article ne se crée par ce geste), il ne se devine pas.
    args_resolus: {
      cs_id: { objet: "consommable", par: ["nom"], requis: [] },
    },
    titre: "Signaler un manque de consommable",
    constatable:
      "le manque apparaît en liste ouverte (écran de contrôle, Suivi équipe, onglet Stock) jusqu'à ce qu'il soit réglé",
    // `texte` couvre les manques SANS produit identifié (`cs_id` absent) — un
    // motif court, borné à 200 caractères, jamais un contenu destiné à un
    // tiers hors de FBZ.
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour: null, // le signalement se clôture par le geste voisin
    ne_fait_pas:
      "il ne commande rien et ne déclenche aucun réapprovisionnement : il ouvre une ligne dans la liste des manques, qui attend qu'on la règle",
    apercu: {
      lecture: "stock_signalements_lire",
      depend_de: ["logement_id", "cs_id"],
      jeton: "x-apercu-version",
    },
    // « au Cocon » désignait deux logements, « draps » et « au Nid »
    // n'existent pas (lot 4) : des phrases sur le parc et le catalogue réels.
    dit_comme: [
      "Il n'y a plus de café au Cocon Naturel",
      "Signale qu'il manque du papier toilette à Sur un Nuage",
      "Note un manque de produit ménager au Havre de Bruguières",
    ],
    vague: 2,
    motif_requis: true,
    // Le nom sous lequel CE handler attend le motif. Sans lui, celui que
    // Dimitri écrit part dans le vide (2026-09-09).
    motif_champ: "texte",
    autonome: false,
  },

  stock_signalement_regler: {
    ecran: "stock",
    endpoint: "/api/beds24-mapping",
    action: "stock_signalement_regler",
    args_autorises: ["id"],
    titre: "Clôturer un signalement de manque",
    constatable: "la ligne quitte la liste des signalements ouverts",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour:
      "la ligne quitte la liste des signalements ouverts et n'y revient pas : pour la rouvrir, il faut signaler de nouveau",
    ne_fait_pas:
      "il ne dépose aucun produit sur place : il dit que le manque est réglé, il ne le règle pas",
    apercu: {
      lecture: "stock_signalements_lire",
      depend_de: ["id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "C'est réglé, j'ai réapprovisionné",
      "Ferme ce signalement de manque",
      "Ce manque-là est traité",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
  },

  // ── INCIDENTS — QUALIFICATION ET RACHAT/DÉPÔT (ADR-227/228, Chantier 42) ─
  //
  // ⚠️ ÉCARTÉE : `orchestrateur_guest_contact` — sert au clic le NOM et le
  // TÉLÉPHONE du voyageur d'une carte. Un voyageur est DEHORS l'entreprise :
  // exposer sa PII n'est pas un « geste » qui change un état applicatif (rien
  // à constater à l'écran hormis la donnée elle-même qui vient de sortir), et
  // l'opérateur n'a aucune raison opérationnelle de PROPOSER cette
  // divulgation — quand Dimitri a besoin de ce numéro, il clique le bouton
  // existant lui-même, en connaissance de cause à cet instant précis. Ce
  // n'est pas un défaut de traçabilité côté handler (la trace fail-closed y
  // est) : c'est que la nature de l'action — servir de la PII voyageur —
  // n'a pas sa place dans un registre de gestes composés à l'avance.
  // ── `incident_degradation_juger` — UN VERDICT DIT, SUR UNE DÉGRADATION ──
  //
  // ⚠️ IL REMPLACE `orchestrateur_qualify_degradations` AU REGISTRE (lot 3 des
  // repères, majeur 1 de la revue). L'ancien levier prenait `statuts`, une
  // table `{ degId → statut }` COMPOSÉE PAR LE MODÈLE : `deg_id` n'a ni lecture
  // qui le rende ni `args_choix`, donc chaque clé était inventée — et le
  // serveur faisait `.map` dessus : une clé inconnue ne matchait rien,
  // l'UPDATE passait, 200 « c'est fait ». « Marque récupérable la tache »
  // écrivait RIEN et répondait oui. Une table complète composée à côté
  // pouvait même sortir l'incident de « en cours » sans avoir jugé ce que
  // Dimitri visait. La voie « le modèle compose les clés » ne survit pas à
  // côté de celle-ci.
  //
  // Même action serveur : elle accepte désormais `{ incident_id, deg_id,
  // verdict }` et reconstruit la table elle-même, sur le libellé canonique ;
  // toute clé inconnue refuse (409). UN verdict par geste — l'incident ne
  // quitte « en cours » qu'au dernier, et c'est le serveur qui le dérive.
  //
  // ⚠️ LE VERDICT EST DIT, JAMAIS DÉDUIT. « la lampe est cassée pour de bon »
  // ne vaut pas « non récupérable » : la liste fermée contraint l'ensemble,
  // pas la source. C'est pourquoi le verdict entre dans l'ÉNONCÉ 1 de la carte
  // (`apercu.valeur_arg`) — il se lit et s'entend AVANT tout clic — et
  // pourquoi ce levier ne part jamais à la voix (`sans_retour`).
  //
  // ⚠️ VAGUE 2 comme son prédécesseur (décision Dimitri 09/09, même nature :
  // il range une décision interne). 0 argent : `cost_eur`, `cout`,
  // `admin_note` restent verrouillés serveur.
  incident_degradation_juger: {
    ecran: "incidents",
    endpoint: "/api/beds24-mapping",
    action: "orchestrateur_qualify_degradations",
    args_autorises: ["incident_id", "deg_id", "verdict"],
    // Deux valeurs, celles du serveur (`VERDICTS`, lib/orchestrateur-handlers.js).
    args_valeurs: { verdict: ["recuperable", "non_recuperable"] },
    // Sans verdict, le bouton reste gris — et le repère refuse avant même la
    // carte (« récupérable, ou non récupérable ? »).
    args_indispensables: ["verdict"],
    // ⚠️ L'INCIDENT D'ABORD, LA DÉGRADATION ENSUITE : l'ordre de déclaration
    // est l'ordre de résolution, et `deg_id` dépend de `incident_id`
    // (`_forme.js` le vérifie). « la lampe » se cherche dans l'incident résolu.
    args_resolus: {
      incident_id: {
        objet: "incident",
        par: ["logement", "quand", "rang"],
        requis: ["logement", "quand"],
      },
      deg_id: {
        objet: "degradation",
        par: ["nom"],
        depend_de: ["incident_id"],
      },
    },
    // ⚠️ « IRRÉCUPÉRABLE », PAS « OU NON » : le titre se lit à voix haute, et
    // « non » est une forme fermée de rétractation (`ANNULE`,
    // src/lib/operateur-acquiescement.js) — un écho suffirait à retirer la
    // carte. L'écran Incidents, lui, garde « Non récupérable ».
    titre: "Juger une dégradation : récupérable ou irrécupérable",
    constatable:
      "sur l'incident, dans l'écran Incidents, la dégradation passe de « à vérifier » à « récupérable » ou « non récupérable » ; quand c'est la dernière à juger, l'incident lui-même passe en réglé ou à racheter, et le propriétaire le voit alors dans son espace",
    sujet: "terrain",
    cout: ["rien"],
    // Affaire interne : personne n'est prévenu. Le propriétaire VERRA
    // l'incident une fois jugé (les « à vérifier » lui sont masqués) — c'est
    // une conséquence, pas un envoi.
    ou_ca_part: ["personne"],
    sans_retour:
      "le dernier verdict ferme l'incident pour de bon : il ne revient pas en « à vérifier », et seul un admin peut encore agir dessus",
    ne_fait_pas:
      "il ne chiffre RIEN et n'engage aucune dépense : le montant du dommage reste verrouillé, il n'appartient qu'à l'administrateur. Il ne juge qu'UNE dégradation à la fois, ne date aucun rachat, et ne prévient ni le voyageur ni le propriétaire",
    apercu: {
      lecture: "incident_degradations_lire",
      depend_de: ["incident_id", "deg_id"],
      jeton: "x-apercu-version",
      // Le verdict est la « valeur suivante » de la carte : il entre dans le
      // fait lu à voix haute (« … à vérifier devient non récupérable »).
      valeur_arg: "verdict",
    },
    dit_comme: [
      "La lampe cassée du Cocon de samedi, c'est non récupérable",
      "Marque récupérable la tache sur l'incident d'hier au Nid",
      "Juge la dégradation du sol de l'incident de mardi : récupérable",
      "La serviette déchirée de l'incident d'hier, on ne la garde pas",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
  },

  // ── `orchestrateur_incident_rachat_depot` ────────────────────────────────
  // Le prompt de cadrage signale à raison que le RACHAT touche l'argent —
  // vérifié sur le code, PAS ICI : le commentaire du handler l'écrit en toutes
  // lettres (« AUCUN MONTANT ne transite ni ne se lit ici »), et le SELECT/UPDATE
  // ne portent que sur `degradations[].racheteLe` / `remplaceLe` (deux dates)
  // + l'identité de qui a posé le geste (serveur, jamais le client). Le
  // montant réel (`cost_eur`) est un champ SÉPARÉ, verrouillé admin, réglé par
  // une action qui n'appartient pas à cette famille. Donc PAS de marquage
  // `argent` ici : il n'y a aucun montant à annoncer dans `constatable`, et en
  // ajouter un serait un marquage vide de sens contraire à sa propre règle
  // (« le MONTANT en toutes lettres, avant le clic »).
  orchestrateur_incident_rachat_depot: {
    ecran: "incidents",
    endpoint: "/api/beds24-mapping",
    action: "orchestrateur_incident_rachat_depot",
    args_autorises: ["incident_id", "deg_id", "geste", "date"],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      geste: ["rachat", "depot"],
    },
    // L'incident par le logement et le jour, la dégradation par son nom dans
    // cet incident (lot 3 des repères) — `deg_id` n'était jusque-là composable
    // que par invention.
    args_resolus: {
      incident_id: {
        objet: "incident",
        par: ["logement", "quand", "rang"],
        requis: ["logement", "quand"],
      },
      deg_id: {
        objet: "degradation",
        par: ["nom"],
        depend_de: ["incident_id"],
      },
    },
    titre: "Dater le rachat d'une dégradation",
    constatable:
      "la date de rachat ou de dépôt apparaît sur la dégradation, dans l'écran Incidents",
    // `geste` ∈ {'rachat','depot'} (liste fermée), `date` = une date
    // OBSERVÉE (YYYY-MM-DD) — exactement « des identifiants et des dates
    // observées », rien d'autre. Possible seulement sur un incident déjà jugé
    // 'a_racheter' (le jugement, lui, est un geste distinct — celui du dessus).
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour: null, // deux dates, reposables — mais voir `ne_fait_pas`
    ne_fait_pas:
      "il n'achète rien et ne déclenche aucune dépense : il DATE un rachat ou un dépôt déjà fait dans la vraie vie. Aucun montant ne transite ni ne se lit ici.",
    apercu: {
      lecture: "incident_degradations_lire",
      depend_de: ["incident_id", "deg_id"],
      jeton: "x-apercu-version",
    },
    dit_comme: [
      "J'ai racheté la lampe de l'incident du Cocon de mardi hier",
      "Note que le remplacement a été déposé lundi",
      "Marque la serviette de l'incident d'hier comme rachetée",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
  },

  // ══ LA CHECKLIST DE MÉNAGE (2026-09-14) ══════════════════════════════════
  //
  // Dimitri : « pose ou ajoute un point de checklist ». Deux gestes, une même
  // action serveur par geste (`lib/checklist-menage-handlers.js`, routées sur
  // `api/beds24-mapping`) — écrites pour l'occasion, sur le patron de
  // « modifie ce champ de cette fiche » : admin seul, jeton `x-cfg-version`,
  // relecture juste avant d'écrire, diff intégral, liste fermée de ce qui peut
  // bouger.
  //
  // ⚠️ LA VISÉE EST L'IDENTIFIANT, ET RIEN D'AUTRE. Le catalogue des champs de
  // fiche tenait la checklist dehors en écrivant « des LISTES d'objets sans
  // identifiant stable » — la prémisse était fausse, et c'est mesuré : l'écran
  // pose un id à chaque pièce et à chaque point (`FicheChecklistCtrl`), et le
  // relevé en base du 2026-09-14 le confirme (lc1 : 5 pièces, 25 points, aucun
  // sans id ; les onze autres logements n'ont AUCUNE checklist). Viser par nom
  // casserait au premier renommage, viser par rang au premier réordonnancement
  // — l'anti-pattern que CLAUDE.md nomme.
  //
  // ⚠️ AUCUNE SUPPRESSION, ET CE N'EST PAS UN OUBLI. E1 : les suppressions
  // sont écartées. Il n'existe ici aucun chemin qui retire une pièce ou un
  // point ; un point devenu inutile se renomme, ou s'efface à la main, devant
  // l'écran.
  //
  // ⚠️ ET UN LOGEMENT SANS CHECKLIST REFUSE, IL NE SE FAIT PAS MEUBLER. Le
  // serveur rend 409 `checklist_absente` plutôt que de pousser les cinq pièces
  // par défaut de l'écran : celles-ci sont une proposition faite à Dimitri
  // devant sa fiche, jamais la conséquence d'un clic sur une carte qui
  // promettait « un point ».
  //
  // ⚠️ VAGUE 9 POUR LES DEUX — le libellé est un TEXTE que Dimitri dicte. C'est
  // l'exception qu'il a lui-même posée : « le jour où il saura proposer un
  // brouillon que je réécris, ça servira ». La borne reste celle d'un argument
  // (120 caractères côté socle, 80 côté serveur) : une consigne de ménage est
  // une ligne, pas un paragraphe — `args_textes` n'est donc PAS déclaré.
  checklist_point_ajouter: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "checklist_point_ajouter",
    methode: "POST",
    args_autorises: ["logement_id", "piece_id", "libelle"],
    // `piece_id` et `libelle` ne sont pas énumérables : les pièces n'existent
    // qu'en base (une par logement, créées par Dimitri), et le libellé est le
    // texte qu'il dicte. Déclaré plutôt que laissé muet, pour qu'on garde la
    // trace le jour où l'un des deux deviendrait vraiment fermé.
    args_libres: ["piece_id", "libelle"],
    apercu: {
      lecture: "checklist_point_lire",
      depend_de: ["logement_id", "piece_id"],
      jeton: "x-cfg-version",
    },
    titre: "Ajouter un point à la checklist de ménage",
    constatable:
      "le point apparaît en fin de liste dans la pièce visée (Logements › fiche › Checklist), et l'agent le verra à cocher sur son téléphone au prochain ménage de ce logement — refusé si ce logement n'a pas encore de checklist, si la pièce n'existe pas, ou si la pièce porte déjà ce libellé",
    sujet: "terrain",
    onglet: "check",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour: null, // on AJOUTE : rien n'est écrasé, et le point se renomme
    ne_fait_pas:
      "il ne crée PAS la liste de ménage d'un logement qui n'en a pas encore : dans ce cas le geste est refusé. Et il ne change rien aux ménages déjà faits — seuls les suivants verront ce point.",
    dit_comme: [
      "Ajoute le lave-vaisselle à vider dans la cuisine",
      "Rajoute un point de ménage dans la salle de bain",
      "Il faut penser au filtre de la hotte, ajoute-le",
    ],
    vague: 9,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (on AJOUTE ; rien ne part, et le point se renomme ensuite) · E2 ✔
    // (visible dans la feuille de pièce juste après) · E3 ✔ (même chemin que
    // l'écran : `checklists.menage.pieces[].items[]`, diff intégral, jeton de
    // version) · E4 ✔ (aucun fait attesté : on écrit une consigne, on ne
    // déclare pas qu'elle a été faite) · E5 ✔.
  },

  checklist_point_renommer: {
    ecran: "logements",
    endpoint: "/api/beds24-mapping",
    action: "checklist_point_renommer",
    methode: "POST",
    args_autorises: ["logement_id", "point_id", "libelle"],
    args_libres: ["point_id", "libelle"],
    apercu: {
      lecture: "checklist_point_lire",
      depend_de: ["logement_id", "point_id"],
      jeton: "x-cfg-version",
    },
    titre: "Renommer un point de la checklist",
    constatable:
      "le point change de libellé dans sa pièce (Logements › fiche › Checklist) — la carte montre l'ancien texte avant le clic ; c'est ce nouveau libellé que l'agent lira au prochain ménage, et les ménages déjà faits gardent le leur",
    sujet: "terrain",
    onglet: "check",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    ne_fait_pas:
      "il ne touche pas aux ménages déjà faits : ceux-là gardent le libellé qu'ils portaient. Et il ne déplace ni ne supprime le point — il n'existe aucun geste de suppression ici (E1).",
    dit_comme: [
      "Reformule ce point de la checklist",
      "Change le texte de cette consigne de ménage",
      "Dis nettoyer la vitre plutôt que laver la vitre",
    ],
    vague: 9,
    motif_requis: false,
    autonome: false,
    // ⚠️ `sans_retour` : renommer ÉCRASE l'ancien libellé, qui n'est stocké
    // nulle part. On peut reposer l'ancien texte ; on ne peut pas « annuler ».
    // C'est la distinction que Dimitri a relevée sur `pricing_unlock` : « je
    // peux annuler » et « je peux poser une autre valeur » ne protègent pas de
    // la même chose.
    sans_retour:
      "L'ancien libellé n'est gardé nulle part : pour revenir en arrière, il faudra le retaper.",
    // E1 ✔ · E2 ✔ · E3 ✔ · E4 ✔ · E5 ✔.
  },

  // ══ LES PORTES DE TERRAIN (2026-09-16, VAGUE 5) ══════════════════════════
  //
  // Dimitri : « Sur ces écrans, il ne peut RIEN faire — même si je dis oui. »
  // Les écrans Ménages, Incidents et Stock écrivaient en direct depuis le
  // navigateur (cadrage § 5.3) : aucune action à composer. Le remède du
  // cadrage : UNE action d'écriture générique par TABLE, sur le patron
  // `logement_champ_set` — catalogue FERMÉ de colonnes, lecture d'aperçu,
  // jeton `x-apercu-version` fail-closed, diff d'une seule clé, compare-and-
  // set + rowcount-check. Les écrans continuent d'écrire comme avant.
  //
  // ⚠️ PLUSIEURS LEVIERS SUR UNE MÊME ACTION, ET C'EST VOULU — le patron
  // `logement_champ_set` / `logement_commission_set`. Un marquage porte sur
  // un levier ENTIER (le montant d'un incident est de l'argent, sa note ne
  // l'est pas) ; et un argument à VALEURS FERMÉES doit les montrer au
  // catalogue (leçon « adresse » vs « adresse_complete ») — « annuler un
  // ménage » énumère ses deux états, « décaler un ménage » laisse l'heure
  // libre. Un seul levier n'aurait pu faire ni l'un ni l'autre.
  //
  // ⚠️ VAGUE 5, FERMÉE PAR DÉFAUT : « le quotidien qui ne sort pas ». Rien ne
  // quitte FBZ ici — l'agent voit son écran, le propriétaire ne reçoit rien,
  // aucune plateforme n'est touchée. S'ouvre par `OPERATEUR_GESTES_VAGUE`
  // (« 5: » candidate, « 5:nom » levier par levier).

  // ── MÉNAGE : menage_run_champ_set (lib/menage-run-champ-handlers.js) ─────
  //
  // ⚠️ `completed` N'EXISTE NULLE PART ICI. « Ménage fait = dû au prestataire »
  // (ADR-193) : le catalogue serveur ne connaît que prévu/annulé, et un ménage
  // terminé refuse toute modification. L'assignation a déjà son levier
  // (`menage_run_assign`, ci-dessus) — pas dupliquée. Le verrou 48 h des
  // agents (ADR-207/208) ne s'applique pas à l'admin : la lecture le DIT,
  // elle ne l'applique pas.
  menage_run_champ_set: {
    ecran: "menages",
    endpoint: "/api/beds24-mapping",
    action: "menage_run_champ_set",
    methode: "POST",
    args_autorises: ["run_id", "champ", "nouvelle"],
    // ⚠️ LE `run_id` NE SE PRONONCE PAS — ET LE MODÈLE NE L'A JAMAIS VU. Ni le
    // relevé (`lib/operateur-jour.js` lit `menage_runs` SANS `id`) ni la
    // lecture d'outil (`lib/lectures-chargeurs.js` le SELECT-e et ne le rend
    // pas) ne le font sortir : un `run_id` composé est forcément inventé. Le
    // modèle pose donc « le ménage du Cocon Naturel samedi » dans `vise`, et
    // `lib/menage-repere.js` va chercher la ligne, avec le jeton de Dimitri.
    // L'argument RESTE dans `args_autorises` (sinon `filtrerArgs` refuse la
    // carte au clic, et l'aperçu ne pourrait plus le citer).
    args_resolus: {
      run_id: { objet: "menage_run", par: ["logement", "quand"] },
    },
    // Recopié du catalogue serveur (un test compare) — `status` a son levier.
    args_valeurs: { champ: ["heure_fixee", "scheduled_date", "notes"] },
    // `nouvelle` est libre par construction : une heure, une date, ou une
    // note que Dimitri dicte (d'où `args_textes`). Le serveur valide contre
    // le catalogue et refuse tout ce qui n'y entre pas — jamais de correction.
    args_libres: ["nouvelle"],
    args_textes: ["nouvelle"],
    apercu: {
      lecture: "menage_run_lire",
      // ⚠️ `nouvelle` VOYAGE AVEC LA LECTURE (revue adverse #1447, C2) : pour
      // un déplacement, ce qui décide n'est pas le verrou du jour ACTUEL du
      // ménage mais celui du jour VISÉ. Sans la cible, la carte annonçait le
      // mauvais verrou et se taisait sur une date passée.
      depend_de: ["run_id", "champ", "nouvelle"],
      jeton: "x-apercu-version",
    },
    titre: "Modifier un ménage : heure, date, notes",
    constatable:
      "le ménage change d'heure, de jour ou de notes dans Ménages, le jour concerné, et l'agent le voit à sa prochaine ouverture ; la carte montre la valeur d'avant — refusé si le ménage est terminé ou annulé",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour:
      "l'ancienne heure, l'ancienne date ou l'ancienne note n'est gardée nulle part : la carte la montre avant le clic, ensuite il faut la retaper",
    // ⚠️ L'HEURE D'ARRIVÉE DU VOYAGEUR N'EST PAS LUE, ET C'EST DIT (revue du
    // 2026-09-22, hors lot). Le cadrage voulait que la carte annonce « le
    // voyageur arrive à 15 h » avant un décalage ; l'heure RÉELLE ne sort que
    // d'`orchestrateur_guest_times` (ADR-226 — `menage_runs.guest_arrival_time`
    // est null par construction), et aucune lecture ne la sert ici. Tant
    // qu'elle n'est pas câblée, la carte le DIT plutôt que de laisser croire
    // qu'elle a vérifié.
    ne_fait_pas:
      "il ne prévient personne, ne change pas l'agent, ne touche pas au coût du ménage, ne vérifie pas l'heure d'arrivée du voyageur ; un ménage fait ne se déplace pas, et jamais vers un jour passé — la saisie de l'agent y serait refusée sans qu'il le sache",
    dit_comme: [
      "Décale le ménage du Cocon à 14 h",
      "Le ménage de samedi passe à dimanche prochain",
      "Note sur ce ménage qu'il faut vérifier la clé",
      "Enlève l'heure fixée sur ce ménage",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (un scalaire, refaisable ; la carte montre l'ancienne valeur) ·
    // E2 ✔ · E3 ✔ (mêmes colonnes que l'écran, une clé par écriture,
    // compare-and-set sur la version lue) · E4 ✔ (rien d'attesté : jamais
    // « terminé ») · E5 ✔ (rowcount-check).
  },

  menage_run_statut_set: {
    ecran: "menages",
    endpoint: "/api/beds24-mapping",
    action: "menage_run_champ_set",
    methode: "POST",
    args_autorises: ["run_id", "champ", "nouvelle"],
    // ⚠️ LE `run_id` NE SE PRONONCE PAS — ET LE MODÈLE NE L'A JAMAIS VU. Ni le
    // relevé (`lib/operateur-jour.js` lit `menage_runs` SANS `id`) ni la
    // lecture d'outil (`lib/lectures-chargeurs.js` le SELECT-e et ne le rend
    // pas) ne le font sortir : un `run_id` composé est forcément inventé. Le
    // modèle pose donc « le ménage du Cocon Naturel samedi » dans `vise`, et
    // `lib/menage-repere.js` va chercher la ligne, avec le jeton de Dimitri.
    // L'argument RESTE dans `args_autorises` (sinon `filtrerArgs` refuse la
    // carte au clic, et l'aperçu ne pourrait plus le citer).
    args_resolus: {
      run_id: { objet: "menage_run", par: ["logement", "quand"] },
    },
    // ⚠️ ET ICI LE REPÈRE PORTE UNE GARDE DE PLUS. « Annulé » ne veut pas dire
    // « pas besoin » : il dit que la RÉSERVATION est annulée, et fait
    // disparaître le passage des lectures, de l'espace du propriétaire et des
    // compteurs du Suivi. Une fois le ménage résolu, `refusSiLeMenageEstDu`
    // relit l'état de la réservation liée et REFUSE la carte tant qu'elle est
    // vivante — un no-show ne se saute pas, le logement a été préparé et le
    // ménage est dû. État illisible ⇒ refus aussi (on n'efface pas sur une
    // ignorance).
    // Les DEUX états que la carte peut poser — « terminé » n'y est pas, et le
    // serveur le refuse aussi. La lecture rend les seules cibles possibles
    // depuis l'état courant ; la carte bloque un choix hors liste avant le clic.
    args_valeurs: { champ: ["status"], nouvelle: ["scheduled", "skipped"] },
    apercu: {
      lecture: "menage_run_lire",
      // `nouvelle` voyage aussi ici (revue #1447, C3) : la lecture retire
      // « annulé » des cibles quand la réservation est encore confirmée, et
      // le dit en clair si c'est justement ce qui a été demandé.
      depend_de: ["run_id", "champ", "nouvelle"],
      jeton: "x-apercu-version",
    },
    titre:
      "Marquer le ménage d'une réservation annulée, ou le remettre au planning",
    constatable:
      "le ménage passe en « annulé » et quitte la liste des agents, ou revient en « prévu » — dans Ménages, le jour concerné ; jamais en « terminé » : un ménage fait se valide sur place, avec ses photos. ⚠️ Un ménage « annulé » disparaît AUSSI de l'espace du propriétaire et ne compte plus dans les compteurs du Suivi équipe",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["agent"],
    sans_retour: null, // prévu ⇄ annulé, dans les deux sens
    ne_fait_pas:
      "il ne marque JAMAIS un ménage comme fait, ne supprime rien, ne prévient personne. Et il ne REPORTE pas : « annulé » dit que la RÉSERVATION est annulée, pas « pas besoin » — sur une réservation confirmée, il refuse (pour un report, déplacez la date)",
    dit_comme: [
      "Le voyageur a annulé, enlève le ménage du Nid",
      "La réservation est tombée, ce ménage n'a plus lieu d'être",
      "Remets le ménage de jeudi au planning",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (réversible) · E2 ✔ · E3 ✔ · E4 ✔ (« terminé » impossible) · E5 ✔.
  },

  // ── INCIDENT : incident_champ_set (lib/incident-champ-handlers.js) ───────
  //
  // Trois leviers, trois colonnes, une action. JAMAIS de suppression (E1) :
  // les deux delete de l'écran restent à l'écran. Le jugement par dégradation
  // garde ses actions propres (`incident_degradation_juger`, plus haut).
  // L'incident se désigne par le logement et le jour (lot 3 des repères) :
  // « ferme l'incident du Cocon de mardi », jamais un identifiant.
  incident_etat_set: {
    ecran: "incidents",
    endpoint: "/api/beds24-mapping",
    action: "incident_champ_set",
    methode: "POST",
    args_autorises: ["incident_id", "champ", "nouvelle"],
    args_resolus: {
      incident_id: {
        objet: "incident",
        par: ["logement", "quand", "rang"],
        requis: ["logement", "quand"],
      },
    },
    // La machine à états ADR-227, côté serveur : « à vérifier » → réglé ou à
    // racheter ; « à racheter » → réglé ; « réglé » est final.
    args_valeurs: { champ: ["status"], nouvelle: ["resolu", "a_racheter"] },
    apercu: {
      lecture: "incident_lire",
      depend_de: ["incident_id", "champ"],
      jeton: "x-apercu-version",
    },
    titre: "Régler un incident, ou le marquer à racheter",
    constatable:
      "l'incident change d'état dans Incidents — « réglé » ou « à racheter » — et la carte ne propose que les sorties possibles depuis son état d'aujourd'hui ; un incident réglé ne revient jamais en arrière, et tant qu'une dégradation n'est pas tranchée la clôture est refusée",
    sujet: "terrain",
    cout: ["rien"],
    // Affaire interne : le propriétaire verra l'incident dans son espace une
    // fois jugé (les « à vérifier » lui sont masqués), mais rien ne PART.
    ou_ca_part: ["personne"],
    sans_retour:
      "« réglé » est un état final : l'incident ne rouvre pas, et « à racheter » ne redevient pas « à vérifier »",
    ne_fait_pas:
      "il ne chiffre rien, ne supprime rien, ne prévient personne. Et il ne JUGE pas les dégradations une par une : tant qu'il en reste une « à vérifier », il refuse de clore — sinon plus personne ne pourrait la trancher",
    dit_comme: [
      "Passe l'incident de mardi au Cocon en réglé",
      "L'incident d'hier au Nid, il faudra racheter",
      "C'est réglé, ferme l'incident du Cocon de samedi",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (pas de suppression ; un état, refaisable vers l'avant) · E2 ✔ ·
    // E3 ✔ (même colonne que les boutons de l'écran) · E4 ✔ · E5 ✔.
  },

  incident_note_set: {
    ecran: "incidents",
    endpoint: "/api/beds24-mapping",
    action: "incident_champ_set",
    methode: "POST",
    args_autorises: ["incident_id", "champ", "nouvelle"],
    args_resolus: {
      incident_id: {
        objet: "incident",
        par: ["logement", "quand", "rang"],
        requis: ["logement", "quand"],
      },
    },
    args_valeurs: { champ: ["admin_note"] },
    // Une note que Dimitri dicte : libre, et jusqu'à 2 000 caractères côté
    // serveur — d'où `args_textes`.
    args_libres: ["nouvelle"],
    args_textes: ["nouvelle"],
    apercu: {
      lecture: "incident_lire",
      depend_de: ["incident_id", "champ"],
      jeton: "x-apercu-version",
    },
    titre: "Écrire la note interne d'un incident",
    constatable:
      "la note apparaît sur l'incident dans Incidents, visible de vous seul — jamais servie aux agents ni au propriétaire ; la carte montre la note d'avant",
    sujet: "terrain",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "la note d'avant est remplacée, pas complétée : pour la garder, il faut la reprendre dans la nouvelle",
    ne_fait_pas:
      "il ne change ni l'état ni le montant de l'incident, et personne n'est prévenu : c'est une note pour vous",
    dit_comme: [
      "Note sur l'incident d'hier au Cocon que j'ai appelé le voyageur",
      "Ajoute une note interne à l'incident de mardi au Nid",
      "Écris sur l'incident de samedi que le proprio est prévenu",
    ],
    vague: 5,
    motif_requis: false,
    autonome: false,
    // E1 ✔ (un texte, refaisable ; l'ancien est montré) · E2 ✔ · E3 ✔ ·
    // E4 ✔ · E5 ✔.
  },

  incident_cout_set: {
    ecran: "incidents",
    endpoint: "/api/beds24-mapping",
    action: "incident_champ_set",
    methode: "POST",
    args_autorises: ["incident_id", "champ", "nouvelle"],
    args_resolus: {
      incident_id: {
        objet: "incident",
        par: ["logement", "quand", "rang"],
        requis: ["logement", "quand"],
      },
    },
    args_valeurs: { champ: ["cost_eur"] },
    args_libres: ["nouvelle"],
    apercu: {
      lecture: "incident_lire",
      depend_de: ["incident_id", "champ"],
      jeton: "x-apercu-version",
    },
    titre: "Fixer le montant d'un incident",
    constatable:
      "le montant apparaît sur l'incident dans Incidents, en euros, et la carte montre l'ancien montant avant le clic ; la justification est écrite au journal AVANT le montant, à partir des faits de l'incident, jamais rédigée — si elle ne peut pas l'être, le montant ne change pas",
    sujet: "terrain",
    cout: ["argent"],
    marquages: ["argent"],
    ou_ca_part: ["personne"],
    sans_retour:
      "l'ancien montant n'est gardé qu'au journal : la fiche, elle, ne montre que le nouveau",
    ne_fait_pas:
      "il ne débite personne, ne touche à aucune caution et n'entre dans AUCUNE facture propriétaire : c'est un chiffre sur la fiche de l'incident et dans la Compta interne, pas un mouvement d'argent — et personne n'est prévenu",
    dit_comme: [
      "L'incident de la lampe au Cocon samedi, c'est 40 euros",
      "Mets le coût de l'incident de mardi au Nid à 120 euros",
      "Le dégât d'hier à Soleil du Sud nous a coûté 35 euros",
    ],
    vague: 5,
    // ⚠️ PAS DE MOTIF COMPOSÉ, ET C'EST LA RÈGLE (décision Dimitri
    // 2026-09-08, lib/geste-motif.js) : la justification d'un montant est
    // FIGÉE par le serveur à partir des faits lus en base — jamais rédigée
    // par le modèle, jamais tapée sur la carte. `motif_requis: false` ici ne
    // veut pas dire « sans motif », il veut dire « le motif n'est pas à toi ».
    motif_requis: false,
    autonome: false,
    // E1 ✔ (un nombre, refaisable ; l'ancien est montré ET journalisé) ·
    // E2 ✔ · E3 ✔ · E4 ✔ (aucun mouvement d'argent : un chiffre sur une
    // fiche) · E5 ✔.
  },

  // ── STOCK : AUCUNE ÉCRITURE ICI, ET C'EST DÉLIBÉRÉ ──────────────────────
  //
  // Cette vague a porté un temps trois leviers de stock (seuil, quantité
  // recalée, dépôt d'un article). Ils ont été RETIRÉS avant fusion, sur
  // contre-revue : « J'ai déposé dix rouleaux au Cocon » composait `qty: 10`
  // dans une unité que le geste ne nommait pas, et le serveur la lisait en
  // sous-paquets — dix rouleaux devenaient soixante rouleaux et 10,78 €
  // imputés au logement en Compta. Un facteur six sur le stock ET sur l'euro,
  // sur la phrase d'exemple du levier lui-même.
  //
  // La famille stock appartient à la PR qui nomme l'unité : un argument
  // `unite` en liste fermée (unité prélevable / paquet / unité finale), une
  // conversion serveur, et un refus franc quand le compte ne tombe pas juste.
  // Deux guichets concurrents sur le même KV (`_bumpStock`), avec deux
  // sémantiques de quantité incompatibles, auraient donné à Dimitri deux
  // boutons pour le même geste — dont un faux.
  //
  // Ce qui RESTE ici : la LECTURE `stock_signalements_lire`, qui sert les
  // leviers de vague 2 déjà en place (signaler un manque, le régler). Elle ne
  // compose aucune quantité.
});

// ══ LES BOOLÉENS DE CETTE FAMILLE, EN MOTS — `levier.argument` ════════════
//
// F5 (03/10/2026) : déplacées telles quelles de `lib/operateur-leviers.js`,
// qui les rassemble (la règle et ses gardes sont écrites là-bas, au-dessus de
// `NOMS_BOOLEENS`). Une phrase se déclare ICI, à côté de son levier, et nulle
// part ailleurs.
const BOOLEENS_LIBELLES = Object.freeze({
  "menage_instruction_archive.archived": {
    oui: "Archiver la consigne",
    non: "Remettre la consigne en service",
  },
});

module.exports = { LEVIERS_MENAGE, BOOLEENS_LIBELLES };
